import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Dialog } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Label, Select } from '@/components/ui/Input';
import { postEmailBlocksPreview, type EmailBlocksPreviewResponse } from '@/lib/api/llm';
import type { EmailTemplates } from '@/pages/campaigns/new/lib/wizard-store';
import type { CampaignContext, ContactFactice } from '@/lib/api/llm';

const PRESETS: { label: string; contact: ContactFactice }[] = [
  {
    label: 'Cabinet RH',
    contact: {
      firstName: 'Sophie',
      lastName: 'Marchal',
      companyName: 'Talent & Co',
      services: ['Recrutement cadres', 'Outplacement', 'Assessment'],
      personas: ['DRH', 'Responsable recrutement'],
      industry: 'Ressources humaines',
      website: 'https://talentco.fr',
    },
  },
  {
    label: 'SaaS B2B',
    contact: {
      firstName: 'Nicolas',
      lastName: 'Dupont',
      companyName: 'FlowOps',
      services: ['Automatisation processus', 'Intégrations SI', 'Analytics'],
      personas: ['DSI', 'Directeur Ops'],
      industry: 'Logiciel B2B',
      website: 'https://flowops.io',
    },
  },
  {
    label: 'Agence pub',
    contact: {
      firstName: 'Laura',
      lastName: 'Bernard',
      companyName: 'Créatif Studio',
      services: ['Création visuelle', 'Campagnes digitales', 'Social media'],
      personas: ['Directeur artistique', 'Chef de projet'],
      industry: 'Communication & Publicité',
      website: 'https://creatifstudio.com',
    },
  },
];

interface Props {
  open: boolean;
  onClose: () => void;
  templates: EmailTemplates;
  campaignContext: CampaignContext;
}

export function EmailPreviewModal({ open, onClose, templates, campaignContext }: Props) {
  const [presetIndex, setPresetIndex] = useState(0);
  const [result, setResult] = useState<EmailBlocksPreviewResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function generate() {
    setLoading(true);
    setError(null);
    try {
      const res = await postEmailBlocksPreview({
        templates,
        contact_factice: PRESETS[presetIndex]!.contact,
        campaign_context: campaignContext,
      });
      setResult(res);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur inconnue');
    } finally {
      setLoading(false);
    }
  }

  function handleClose() {
    setResult(null);
    setError(null);
    onClose();
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title="Preview IA — emails générés"
      description="Aperçu de la séquence email pour un contact factice. La génération réelle se fera per-contact au moment du push."
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={handleClose}>
            Fermer
          </Button>
          <Button onClick={generate} loading={loading} data-testid="generate-preview-btn">
            {loading ? 'Génération…' : 'Générer'}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        {/* Contact selector */}
        <div className="space-y-1">
          <Label htmlFor="preset-select">Contact factice</Label>
          <Select
            id="preset-select"
            value={String(presetIndex)}
            onChange={(e) => {
              setPresetIndex(Number(e.target.value));
              setResult(null);
            }}
            data-testid="preset-select"
          >
            {PRESETS.map((p, i) => (
              <option key={i} value={i}>
                {p.label} — {p.contact.firstName} {p.contact.lastName} ({p.contact.companyName})
              </option>
            ))}
          </Select>
        </div>

        {error && (
          <p className="text-sm text-red-500" data-testid="preview-error">
            {error}
          </p>
        )}

        {loading && (
          <div className="flex items-center justify-center py-8 text-gray-400">
            <Loader2 size={24} className="animate-spin mr-2" />
            <span className="text-sm">Génération en cours…</span>
          </div>
        )}

        {result && !loading && (
          <div className="space-y-4" data-testid="preview-results">
            {(
              [
                {
                  label: 'Email 1 — Premier contact',
                  subject: result.email01_subject,
                  body: result.email01_body_rendered,
                  testId: 'preview-email01',
                },
                {
                  label: 'Email 2 — Relance',
                  subject: result.email02_subject,
                  body: result.email02_body_rendered,
                  testId: 'preview-email02',
                },
                {
                  label: 'Email 3 — Dernier contact',
                  subject: result.email03_subject,
                  body: result.email03_body_rendered,
                  testId: 'preview-email03',
                },
              ] as const
            ).map((email) => (
              <div
                key={email.testId}
                className="border border-brand-border rounded-brand p-4 space-y-2"
                data-testid={email.testId}
              >
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  {email.label}
                </p>
                <p className="text-sm font-medium text-brand-dark">
                  <span className="text-gray-400 font-normal">Sujet : </span>
                  {email.subject}
                </p>
                <pre className="text-sm text-gray-700 whitespace-pre-wrap font-sans leading-relaxed border-t border-brand-border pt-2">
                  {email.body}
                </pre>
              </div>
            ))}
            <p className="text-xs text-gray-400 text-center">
              Généré avec {result.generation_metadata.model} — {result.generation_metadata.tokens} tokens (
              {(result.generation_metadata.cost * 100).toFixed(4)} ¢)
            </p>
          </div>
        )}

        {!result && !loading && !error && (
          <p className="text-sm text-gray-400 text-center py-6" data-testid="preview-empty">
            Sélectionnez un contact factice et cliquez sur "Générer" pour voir un aperçu.
          </p>
        )}
      </div>
    </Dialog>
  );
}
